/**
 * Shared Ask Insights session — used by `/ai` and the Subject panel.
 */

import {
  AiArtifactListResponseSchema,
  AiConversationDetailSchema,
  AiSourceSearchResponseSchema,
  type AiAttachmentRef,
  type AiSourceSearchResponse,
} from "@l30/shared";
import { Button } from "@l30/ui";
import { useIdeationMaps } from "@l30/web-atlas/api/ideation-maps";
import { Composer, MessageBubble } from "@l30/web-atlas/chat-ui";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  cancelAiToolConfirmation,
  uploadAiAttachment,
  useAiConversations,
  useAiSkills,
  useAiUsageMe,
  useConfirmAiTool,
  useCreateAiArtifact,
  useCreateAiConversation,
  useStreamingAiChat,
} from "../../api/ai.js";
import { apiFetch } from "../../api/client.js";
import { useFeatureFlags } from "../../api/feature-flags.js";
import { useWorkspaceDocumentEditUrl } from "../../api/workspaces.js";

import { AiArtefactsRail } from "./AiArtefactsRail.js";
import { AiConfirmBanner } from "./AiConfirmBanner.js";
import { AiHistoryRail } from "./AiHistoryRail.js";
import { AiShell } from "./AiShell.js";
import { AiSourcesStrip } from "./AiSourcesStrip.js";
import { AiStatusLine } from "./AiStatusLine.js";
import { ArtefactPreview } from "./ArtefactPreview.js";
import { ArtifactPane } from "./ArtifactPane.js";
import { AskCitationChips } from "./AskCitationChips.js";
import { confirmFailureOutcome } from "./confirm-outcome.js";
import { isLockedAskSource, withLockedPrimary, type AskSourceChip } from "./locked-ask-source.js";
import { decisionTargetFromSources, RecordDecisionAction } from "./RecordDecisionAction.js";
import { pinnedSourcesFromUrl } from "./source-labels.js";

function detectArtefactKind(bodyMd: string): "checklist" | "note" {
  if (/```(?:json\s+)?checklist\b/i.test(bodyMd)) return "checklist";
  return "note";
}

export type AskInsightsLayout = "page" | "subject-panel";

export interface AskInsightsSessionProps {
  layout: AskInsightsLayout;
  initialSources?: AskSourceChip[];
  lockedPrimary?: AskSourceChip;
  hideImplicitRag?: boolean;
  composerSeed?: string;
  heading?: string;
  emptyHint?: string;
  autoFocusComposer?: boolean;
}

/* eslint-disable max-lines-per-function, complexity -- extracted from AiPage; one session owner */
export function AskInsightsSession({
  layout,
  initialSources,
  lockedPrimary,
  hideImplicitRag = false,
  composerSeed,
  heading,
  emptyHint,
  autoFocusComposer = false,
}: AskInsightsSessionProps) {
  const page = layout === "page";
  const flagsQuery = useFeatureFlags();
  const flagOn = useMemo(
    () => flagsQuery.data?.flags.find((f) => f.key === "insights_ai")?.enabled === true,
    [flagsQuery.data],
  );
  const ragFlag = useMemo(
    () => flagsQuery.data?.flags.find((f) => f.key === "insights_ai_rag")?.enabled === true,
    [flagsQuery.data],
  );

  const skillsQuery = useAiSkills(flagOn && page);
  const conversationsQuery = useAiConversations(flagOn && page);
  const usageQuery = useAiUsageMe(flagOn && page);
  const mapsQuery = useIdeationMaps();
  const createConversation = useCreateAiConversation();
  const createArtifact = useCreateAiArtifact();
  const {
    messages,
    isStreaming,
    statusLabel,
    conversationId,
    lastAssistantId,
    pendingConfirms,
    ask,
    reset,
    hydrate,
    setConversationId,
    cancel,
    clearPendingConfirms,
  } = useStreamingAiChat();
  const confirmTool = useConfirmAiTool();
  const editUrl = useWorkspaceDocumentEditUrl();

  const [sources, setSources] = useState<AskSourceChip[]>(() =>
    withLockedPrimary(initialSources ?? (page ? pinnedSourcesFromUrl() : []), lockedPrimary),
  );
  const [pickerQ, setPickerQ] = useState("");
  const [pickerHits, setPickerHits] = useState<AiSourceSearchResponse["hits"]>([]);
  const [ragEnabled, setRagEnabled] = useState(!hideImplicitRag);
  const [personalMode, setPersonalMode] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<AiAttachmentRef[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [railOpen, setRailOpen] = useState(false);
  const [artifacts, setArtifacts] = useState<
    Array<{ id: string; title: string; bodyMd: string; bodyJsx: string | null; kind: string }>
  >([]);
  const [preview, setPreview] = useState<{
    title: string;
    bodyMd: string;
    bodyJsx: string | null;
    kind: string;
  } | null>(null);
  const [artifactRuntimeJs, setArtifactRuntimeJs] = useState<string | null>(null);
  const pinnedSources = withLockedPrimary(sources, lockedPrimary);

  useEffect(() => {
    if (!autoFocusComposer) return;
    const id = window.setTimeout(() => {
      const input = document.querySelector<HTMLTextAreaElement>(
        'textarea[aria-label="Ask a question"]',
      );
      input?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [autoFocusComposer]);

  useEffect(() => {
    if (!page) return undefined;
    let cancelled = false;
    void fetch("/artifact-runtime/runtime.js")
      .then((res) => {
        if (!res.ok) throw new Error(`runtime HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setArtifactRuntimeJs(text);
      })
      .catch(() => {
        if (!cancelled) setArtifactRuntimeJs("");
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  useEffect(() => {
    if (!pickerQ.trim() || !flagOn) return;
    const t = setTimeout(() => {
      void apiFetch(
        `/ai/sources/search?q=${encodeURIComponent(pickerQ.trim())}&limit=12`,
        {},
        AiSourceSearchResponseSchema,
      )
        .then((r) => setPickerHits(r.hits))
        .catch(() => setPickerHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [pickerQ, flagOn]);

  useEffect(() => {
    if (!conversationId || !page) return;
    let cancelled = false;
    void apiFetch(`/ai/conversations/${conversationId}/artifacts`, {}, AiArtifactListResponseSchema)
      .then((r) => {
        if (cancelled) return;
        setArtifacts(
          r.artifacts.map((a) => ({
            id: a.id,
            title: a.title,
            bodyMd: a.bodyMd,
            bodyJsx: a.bodyJsx,
            kind: a.kind,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setArtifacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, lastAssistantId, page]);

  const visibleArtifacts = conversationId && page ? artifacts : [];
  const visiblePickerHits = pickerQ.trim() && flagOn ? pickerHits : [];
  const ragForTurn = hideImplicitRag ? false : ragEnabled;

  if (flagsQuery.isLoading) {
    return <p className="text-sm text-muted-fg">Loading…</p>;
  }

  if (!flagOn) {
    return (
      <section className="mx-auto max-w-xl space-y-3 px-3 py-12">
        <h1 className="text-2xl font-semibold text-fg">Ask Insights</h1>
        <p className="text-sm text-muted-fg">
          Insights AI is not enabled. Ask an admin to enable{" "}
          <code className="text-xs">insights_ai</code>.
        </p>
      </section>
    );
  }

  const alwaysOn = (skillsQuery.data?.skills ?? []).filter((s) => s.alwaysOn);
  const decisionTarget = decisionTargetFromSources(pinnedSources);
  const lastAssistantIndex = messages.reduce(
    (latest, message, index) => (message.role === "assistant" ? index : latest),
    -1,
  );
  const seededTitle = lockedPrimary?.title ?? pinnedSources[0]?.title;
  const subjectEmpty =
    (lockedPrimary?.type ?? pinnedSources[0]?.type) === "subject" && Boolean(seededTitle);

  const ensureConversation = async (): Promise<string> => {
    if (conversationId) return conversationId;
    const created = await createConversation.mutateAsync({
      title: personalMode ? "Personal" : (seededTitle ?? "New conversation"),
      sources: pinnedSources.map(({ type, id }) => ({ type, id })),
      ragEnabled: ragForTurn,
    });
    setConversationId(created.id);
    return created.id;
  };

  const onAttach = async (file: File) => {
    try {
      setAttaching(true);
      const id = await ensureConversation();
      const ref = await uploadAiAttachment(id, file);
      setPendingAttachments((prev) => [...prev, ref].slice(0, 10));
      toast.success(`Attached ${file.name}`);
    } catch {
      toast.error("Could not upload that file.");
    } finally {
      setAttaching(false);
    }
  };

  const submit = async (content: string) => {
    try {
      const id = await ensureConversation();
      const attachments = pendingAttachments;
      setPendingAttachments([]);
      ask({
        conversationId: id,
        content,
        sources: pinnedSources.map(({ type, id: sid }) => ({ type, id: sid })),
        ragEnabled: ragForTurn,
        attachments: attachments.length > 0 ? attachments : undefined,
        skillIds: personalMode ? ["personal-sidecar"] : undefined,
      });
    } catch {
      toast.error("Could not start the conversation.");
    }
  };

  const onSaveArtefact = async () => {
    if (!conversationId) {
      toast.error("Start a conversation first.");
      return;
    }
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant" && m.content);
    if (!lastAssistant) {
      toast.error("Nothing to save yet.");
      return;
    }
    const kind = detectArtefactKind(lastAssistant.content);
    try {
      await createArtifact.mutateAsync({
        conversationId,
        messageId: lastAssistantId ?? lastAssistant.messageId,
        kind,
        title: lastAssistant.content.slice(0, 80).replace(/\n/g, " ") || "Insights AI artefact",
        bodyMd: lastAssistant.content,
      });
      toast.success(kind === "checklist" ? "Checklist artefact saved." : "Artefact saved.");
      const r = await apiFetch(
        `/ai/conversations/${conversationId}/artifacts`,
        {},
        AiArtifactListResponseSchema,
      );
      setArtifacts(
        r.artifacts.map((a) => ({
          id: a.id,
          title: a.title,
          bodyMd: a.bodyMd,
          bodyJsx: a.bodyJsx,
          kind: a.kind,
        })),
      );
    } catch {
      toast.error("Could not save the artefact.");
    }
  };

  const onCopyArtefact = async (bodyMd: string) => {
    try {
      await navigator.clipboard.writeText(bodyMd);
      toast.success("Copied markdown");
    } catch {
      toast.error("Copy failed");
    }
  };

  const onDownloadArtefact = (title: string, bodyMd: string) => {
    const blob = new Blob([bodyMd], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^\w.-]+/g, "_").slice(0, 80) || "artefact"}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onResume = async (id: string) => {
    try {
      const detail = await apiFetch(`/ai/conversations/${id}`, {}, AiConversationDetailSchema);
      hydrate(detail);
      setSources(
        withLockedPrimary(
          (detail.conversation.sourceScope ?? []).map((s) => ({
            ...s,
            title: undefined,
          })),
          lockedPrimary,
        ),
      );
      setPendingAttachments([]);
      clearPendingConfirms();
      setRailOpen(false);
    } catch {
      toast.error("Could not load that conversation.");
    }
  };

  const onConfirmTool = async (pending: {
    toolId: string;
    args: Record<string, unknown>;
    confirmationToken: string;
  }) => {
    if (!conversationId) {
      toast.error("Start a conversation first.");
      return;
    }
    try {
      const res = await confirmTool.mutateAsync({
        confirmationToken: pending.confirmationToken,
        messageId: lastAssistantId ?? undefined,
      });
      if (res.decision === "allow") {
        toast.success(res.summary ?? "Confirmed.");
      } else {
        toast.error(res.summary ?? `Tool ${res.decision}`);
      }
      // The server answered, so the single-use token is spent either way.
      clearPendingConfirms();
    } catch (err) {
      const outcome = confirmFailureOutcome(err);
      if (outcome.clearPending) clearPendingConfirms();
      toast.error(outcome.message);
    }
  };

  const onConfirmLegacyTool = async (toolId: "record_decision", args: Record<string, unknown>) => {
    if (!conversationId) {
      toast.error("Start a conversation first.");
      return;
    }
    try {
      const res = await confirmTool.mutateAsync({
        conversationId,
        toolId,
        args,
        messageId: lastAssistantId ?? undefined,
      });
      if (res.decision === "allow") toast.success(res.summary ?? "Confirmed.");
      else toast.error(res.summary ?? `Tool ${res.decision}`);
    } catch {
      toast.error("Could not confirm that action.");
    }
  };

  const onOpenInOffice = async (documentId: string) => {
    try {
      const { url, storageLocation } = await editUrl.mutateAsync(documentId);
      if (storageLocation !== "sharepoint") {
        toast.message("Not a SharePoint Office file — opening download instead.");
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Could not open in Office.");
    }
  };

  const addSource = (hit: AiSourceSearchResponse["hits"][number]) => {
    setSources((prev) => {
      if (prev.some((s) => s.type === hit.type && s.id === hit.id)) return prev;
      return withLockedPrimary(
        [...prev, { type: hit.type, id: hit.id, title: hit.title, subtitle: hit.subtitle }],
        lockedPrimary,
      );
    });
  };

  const pinKnowledgeProject = (map: { id: string; title: string }) => {
    setSources((prev) => {
      if (prev.some((s) => s.type === "ideation_map" && s.id === map.id)) return prev;
      return withLockedPrimary(
        [...prev, { type: "ideation_map" as const, id: map.id, title: map.title }],
        lockedPrimary,
      );
    });
  };

  const startKnowledgeProjectChat = async (map: { id: string; title: string }) => {
    try {
      reset();
      setPendingAttachments([]);
      const pinned = [{ type: "ideation_map" as const, id: map.id, title: map.title }];
      setSources(pinned);
      const created = await createConversation.mutateAsync({
        title: map.title,
        sources: [{ type: "ideation_map", id: map.id }],
        ragEnabled: ragForTurn,
      });
      setConversationId(created.id);
      toast.success(`Opened Knowledge project: ${map.title}`);
    } catch {
      toast.error("Could not open that Knowledge project.");
    }
  };

  const onNewChat = () => {
    reset();
    setPendingAttachments([]);
    clearPendingConfirms();
    setSources((prev) => withLockedPrimary(prev, lockedPrimary));
  };

  const showArtefacts = page && (visibleArtifacts.length > 0 || preview !== null);

  const removeSource = (type: string, id: string) => {
    if (isLockedAskSource({ type, id }, lockedPrimary)) return;
    setSources((prev) => prev.filter((x) => !(x.type === type && x.id === id)));
  };

  const messagesPane = (
    <div className="space-y-3">
      {messages.length === 0 ? (
        <div
          className={
            page
              ? "flex min-h-[40vh] flex-col items-center justify-center gap-2 px-4 text-center"
              : "flex flex-col gap-2 px-1 py-6 text-center"
          }
        >
          <p className="text-sm text-fg">
            {heading
              ? heading
              : subjectEmpty
                ? `Ask about ${seededTitle}`
                : seededTitle
                  ? `Pinned: ${seededTitle}. Ask a question below.`
                  : "Ask a question below."}
          </p>
          <p className="max-w-md text-xs text-muted-fg">
            {emptyHint ??
              "Add sources under the composer to ground the answer. Use the paperclip to attach files or screenshots. Sources are kept when you start a new chat."}
          </p>
        </div>
      ) : null}
      {messages.map((m, i) => (
        <div key={`${m.role}-${i}`} className="space-y-1">
          <MessageBubble
            role={m.role}
            content={m.content || (isStreaming && i === messages.length - 1 ? "…" : "")}
          />
          {m.role === "assistant" && m.citations && m.citations.length > 0 ? (
            <AskCitationChips citations={m.citations} />
          ) : null}
          {page &&
          m.role === "assistant" &&
          m.messageId &&
          i === lastAssistantIndex &&
          decisionTarget ? (
            <div className="pl-1">
              <RecordDecisionAction
                mapId={decisionTarget.mapId}
                documentIds={decisionTarget.documentIds}
                disabled={confirmTool.isPending}
                onConfirm={(args) => void onConfirmLegacyTool("record_decision", args)}
              />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );

  const footer = (
    <>
      <AiStatusLine label={statusLabel} />
      <AiConfirmBanner
        pending={pendingConfirms}
        confirming={confirmTool.isPending}
        onApprove={(pending) => void onConfirmTool(pending)}
        onDismiss={() => {
          for (const item of pendingConfirms) {
            void cancelAiToolConfirmation(item.confirmationToken).catch(() => undefined);
          }
          clearPendingConfirms();
        }}
      />
      <AiSourcesStrip
        sources={pinnedSources}
        attachments={pendingAttachments}
        pickerQ={pickerQ}
        onPickerQChange={(q) => {
          setPickerQ(q);
          if (!q.trim()) setPickerHits([]);
        }}
        pickerHits={visiblePickerHits}
        maps={(mapsQuery.data?.maps ?? []).map((m) => ({ id: m.id, title: m.title }))}
        mapsLoading={mapsQuery.isLoading}
        mapsError={mapsQuery.isError}
        ragFlag={Boolean(ragFlag)}
        ragEnabled={ragEnabled}
        onRagEnabledChange={setRagEnabled}
        personalMode={personalMode}
        onPersonalModeChange={setPersonalMode}
        onAddSource={addSource}
        onRemoveSource={removeSource}
        onRemoveAttachment={(key) =>
          setPendingAttachments((prev) => prev.filter((x) => x.key !== key))
        }
        onAddKnowledgeMap={pinKnowledgeProject}
        onNewChatInMap={page ? (map) => void startKnowledgeProjectChat(map) : undefined}
        onOpenInOffice={(id) => void onOpenInOffice(id)}
        lockedSource={lockedPrimary}
        hideRag={hideImplicitRag}
      />
      <Composer
        disabled={isStreaming || attaching}
        initialValue={composerSeed}
        onSubmit={(q) => void submit(q)}
        onCancel={() => cancel?.()}
        onAttach={(file) => void onAttach(file)}
        attaching={attaching}
      />
    </>
  );

  if (!page) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-3">{messagesPane}</div>
        <div className="shrink-0 space-y-2 border-t border-border bg-surface p-3">{footer}</div>
      </div>
    );
  }

  const history = (
    <AiHistoryRail
      conversations={conversationsQuery.data?.conversations ?? []}
      onSelect={(id) => void onResume(id)}
      onNewChat={onNewChat}
    />
  );

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold text-fg sm:text-2xl">Ask Insights</h1>
        <p className="text-xs text-muted-fg sm:text-sm">
          Skills: {alwaysOn.map((s) => s.name).join(" · ") || "…"}
          {personalMode ? " · Personal" : null}
          {usageQuery.data
            ? ` · ${usageQuery.data.requestCount} req · ${usageQuery.data.inputTokens + usageQuery.data.outputTokens} tok`
            : null}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="lg:hidden"
          onClick={() => setRailOpen((v) => !v)}
        >
          {railOpen ? "Hide history" : "History"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onNewChat}>
          New chat
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!messages.some((m) => m.role === "assistant" && m.content)}
          onClick={() => void onSaveArtefact()}
        >
          Save artefact
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <AiShell
        header={header}
        history={history}
        mobileHistoryOpen={railOpen}
        showArtefacts={showArtefacts}
        artefacts={
          <AiArtefactsRail
            artifacts={visibleArtifacts}
            onOpen={(id) => {
              const a = visibleArtifacts.find((x) => x.id === id);
              if (!a) return;
              setPreview({
                title: a.title,
                bodyMd: a.bodyMd,
                bodyJsx: a.bodyJsx,
                kind: a.kind,
              });
            }}
          />
        }
        messages={messagesPane}
        footer={footer}
      />

      {preview !== null &&
        preview.kind === "interactive" &&
        preview.bodyJsx != null &&
        preview.bodyJsx.trim() !== "" && (
          <ArtifactPane
            title={preview.title}
            bodyMd={preview.bodyMd}
            bodyJsx={preview.bodyJsx}
            runtimeJs={artifactRuntimeJs}
            onClose={() => setPreview(null)}
            onCopyJsx={() => void onCopyArtefact(preview.bodyJsx ?? "")}
            onCopyNotes={() => void onCopyArtefact(preview.bodyMd)}
          />
        )}

      {preview !== null && preview.kind !== "interactive" && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Artefact preview"
        >
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-md border border-border bg-surface p-4 sm:rounded-md">
            <div className="mb-2 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void onCopyArtefact(preview.bodyMd)}
              >
                Copy
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onDownloadArtefact("artefact", preview.bodyMd)}
              >
                Download .md
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setPreview(null)}>
                Close
              </Button>
            </div>
            <ArtefactPreview bodyMd={preview.bodyMd} kind={preview.kind} />
          </div>
        </div>
      )}
    </>
  );
}
/* eslint-enable max-lines-per-function, complexity -- extracted Ask session from AiPage */
